import { Router } from '@angular/router';
import { UserType } from 'identity-management';
import { environment } from '../environments/environment';
import { LobLinkEntryService, ProductsService, UserService } from 'shared-data-access';
import { SessionHandlerService } from './services/session-handler/session-handler.service';
import { FeatureToggleService } from 'shared-data-access';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AppRoutes, LanguageCode, LanguageService, SessionStorageService } from 'shared-utils';
import { LinksService } from 'external-links-data-access';
import { AuthService } from 'auth';

export function initializeApp(
  authService: AuthService,
  userService: UserService,
  sessionHandlerService: SessionHandlerService,
  router: Router,
  sessionStorageService: SessionStorageService,
  lobLinkEntryService: LobLinkEntryService,
  productsService: ProductsService,
  featureToggleService: FeatureToggleService,
  translateService: TranslateService,
  linksService: LinksService
): () => Promise<void | boolean> {
  return async (): Promise<void | boolean> => {
    if (!productsService.isProductRoute) {
      productsService.clearJourneyState();
    }

    lobLinkEntryService.saveUrlParamsToSessionStorage();

    await loginUser();
    const freshToken: boolean = await authService.isAuthenticated();
    const isSessionStorageFlow: boolean = freshToken || isClientInformationInSessionStorage();

    if (!isSessionStorageFlow) return false;

    await setAppLanguage();

    await featureToggleService.loadFeatureToggleListAndSave();
    const isB2b2cFlow: boolean = freshToken && shouldNavigateToB2b2cFlow();

    initOneTrust(featureToggleService);
    initAdobeLaunch(featureToggleService);
    await firstValueFrom(linksService.getLinks());
    await redirectUser(isB2b2cFlow, isSessionStorageFlow);
  };

  async function redirectUser(isB2b2cFlow: boolean, isSessionStorageFlow: boolean): Promise<void | boolean> {
    if (isB2b2cFlow) {
      await router.navigate(['/', AppRoutes.Floating]);
      return;
    }

    if (isSessionStorageFlow) {
      sessionHandlerService.updateIdleTerminateTime();
      sessionHandlerService.updateExpireTime();

      if (lobLinkEntryService.isQueryParamsLobEntry()) {
        return lastValueFrom<boolean>(lobLinkEntryService.initJourneyEntry());
      }

      userService.setFirstRouteInfo();
    }
  }

  async function loginUser(): Promise<void> {
    authService.setConfiguration(environment);
    await authService.checkAfterLoginURL();
  }

  function shouldNavigateToB2b2cFlow(): boolean {
    return userService.getTokenInfo().link_id && userService.getTokenInfo().user_type === UserType.Customer;
  }

  async function setAppLanguage(): Promise<void> {
    const userLanguage: LanguageCode = userService.getLanguage();
    const availableLanguages: LanguageCode[] = userService.getAvailableLanguages();
    const selectedLanguage: LanguageCode =
      availableLanguages.length && !availableLanguages.includes(userLanguage) ? availableLanguages[0] : userLanguage;

    if (selectedLanguage !== LanguageService.getPathnameLanguage()) {
      LanguageService.changeAppLanguage(selectedLanguage);
    }

    userService.setLanguage(selectedLanguage);
    translateService.setDefaultLang(selectedLanguage);
    await lastValueFrom(translateService.use(selectedLanguage));
  }

  function isClientInformationInSessionStorage(): boolean {
    const sessionStorageHasTokenInfo = !!sessionStorageService.get(environment.forgeRock.clientId);

    sessionStorageService.remove(environment.forgeRock.clientId);
    return sessionStorageHasTokenInfo;
  }
}

function initAdobeLaunch(featureToggleService: FeatureToggleService): void {
  const src: string = environment.adobeLaunchScriptUrl;

  if (!src || !featureToggleService.getFeatureConfig('adobeAnalytics')) return;

  window['adobeDataLayer'] = window['adobeDataLayer'] || [];

  const script: HTMLScriptElement = document.createElement('script');

  script.src = src;
  script.async = true;
  document.head.appendChild(script);
}

function initOneTrust(featureToggleService: FeatureToggleService): void {
  if (!featureToggleService.getFeatureConfig('cookieBanner')) return;

  const src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
  const domainScript: string = environment.oneTrustId;

  const script: HTMLScriptElement = document.createElement('script');

  script.src = src;
  script.type = 'text/javascript';
  script.setAttribute('data-domain-script', domainScript);

  script.async = true;

  document.head.appendChild(script);
}
